/*  ========================================================================
    # Sections - Team
    ========================================================================  */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import useLocalization from '../../hooks/useLocalization';
import useBreakpoints from '../../hooks/useBreakpoints';
import useTranslation from '../../hooks/useTranslation';
import tailwindConfig from '../../../../tailwind.config';
import bem from '../../helpers/bem';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';

const Team = ({
  heading,
  button,
  entries
}) => {
  const appContainerRef = useRef(null);
  const headerRef = useRef(null);
  const { smUp, mdUp, lgUp, xlUp, xxlUp } = useBreakpoints();
  const [activeSlide, setActiveSlide] = useState(0);
  const [clickedElement, setClickedElement] = useState(null);
  const [oneRow, setOneRow] = useState(true);
  const __ = useTranslation();

  // Icons
  const localize = useLocalization();
  const iconArrowLeft = `${localize('svgSprite')}#arrow-left`;
  const iconArrowRight = `${localize('svgSprite')}#arrow-right`;

  const shuffledEntries = useMemo(() => {
    return [...entries].sort(() => 0.5 - Math.random());
  }, [entries]);

  // Set the visible slides corresponding to the current breakpoint.
  const visibleSlides = useMemo(() => {
    if (xxlUp) return 5;
    if (xlUp) return 4;
    if (lgUp) return 3;
    if (mdUp) return 2;
    if (smUp) return 2;
    return 1;
  });

  // Set the maximum slide index.
  const maxIndex = (oneRow ? shuffledEntries.length : (Math.round(shuffledEntries.length / 2))) - visibleSlides;

  // Navigate Left
  const canNavigateLeft = activeSlide > 0;
  const navigateLeft = () => {
    setClickedElement(null);
    if (canNavigateLeft) setActiveSlide(activeSlide - 1);
  };

  // Navigate Right
  const canNavigateRight = activeSlide < maxIndex;
  const navigateRight = () => {
    setClickedElement(null);
    if (canNavigateRight) setActiveSlide(activeSlide + 1);
  };

  // Add swipe handlers.
  const swipeHandlers = useSwipeable({
    onSwipedRight: navigateLeft,
    onSwipedLeft: navigateRight
  });

  // Set the correct active slide when resizing the window.
  useEffect(() => {
    const visibleSlides = () => {
      if (window.innerWidth > parseInt(tailwindConfig.theme.screens.xxl)) return 5;
      if (window.innerWidth > parseInt(tailwindConfig.theme.screens.xl)) return 4;
      if (window.innerWidth > parseInt(tailwindConfig.theme.screens.lg)) return 3;
      if (window.innerWidth > parseInt(tailwindConfig.theme.screens.md)) return 2;
      if (window.innerWidth > parseInt(tailwindConfig.theme.screens.sm)) return 2;
      return 1;
    }

    const checkRowCount = () => {
      let marginBottom = window.getComputedStyle(headerRef.current).getPropertyValue('margin-bottom');
      let shouldHeight = headerRef.current.clientHeight + parseInt(marginBottom) + appContainerRef.current.clientWidth / visibleSlides() * 2 + 100;
      if (window.innerHeight > shouldHeight) {
        setOneRow(false);
      } else {
        setOneRow(true);
      }
    };

    window.addEventListener(
      'resize',
      () => {
        checkRowCount();
      },
      { passive: true }
    );
  }, []);

  useEffect(() => {
    // set the active slide to 0 if the visible slides change
    setActiveSlide(0);
  }, [visibleSlides]);

  useEffect(() => {
    // if the oneRow changed to double row (oneRow == false)
    // set the active slides to max, so no white space appears on the right
    if (!oneRow)
      if (activeSlide > maxIndex)
        setActiveSlide(maxIndex);
  }, [oneRow]);

  const toggleInfo = element => {
    let html = document.getElementsByClassName('device-touch');
    if (html.length > 0)
      if (clickedElement === element) setClickedElement(null);
      else setClickedElement(element);
  };

  return (
    <div ref={appContainerRef}>
      <div className='container'>
          <div className={bem('s-team', 'header')} ref={headerRef}>
            <h3 dangerouslySetInnerHTML={{ __html: heading }} />
            <div className={bem('s-team', 'header-button-container')}>
              <a className={bem('c-button', '', { black: true })} href={button.url}>
                {button.title}
              </a>
              <div className={bem('s-team', 'header-controls')}>
                <button
                  className={bem('c-button', '', {
                    black: true,
                    icon: true,
                    inactive: !canNavigateLeft
                  })}
                  title={__('section-team-navigate-left')}
                  aria-label={__('section-team-navigate-left')}
                  onClick={navigateLeft}
                >
                  <svg viewBox='0 0 200 200'>
                    <use href={iconArrowLeft}></use>
                  </svg>
                </button>
                <button
                  className={bem('c-button', '', {
                    black: true,
                    icon: true,
                    inactive: !canNavigateRight
                  })}
                  title={__('section-team-navigate-right')}
                  aria-label={__('section-team-navigate-right')}
                  onClick={navigateRight}
                >
                  <svg viewBox='0 0 200 200'>
                    <use href={iconArrowRight}></use>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

      <div className={bem('s-team', 'wrapper')}>
          <div className={bem('s-team', 'slider', { oneRow: oneRow })}
            style={{
              '--visible-slides': visibleSlides,
              '--slide-offset': activeSlide
            }}
            {...swipeHandlers}>
            {shuffledEntries.map((entry, index) => (
              <div key={index} className={bem('s-team', 'slide', { active: index == activeSlide })}>
                <div
                  key={`${entry.name}-${index}`}
                  className={bem('s-team', 'entry', { hover: entry.description, active: entry === clickedElement })}
                  onClick={() => toggleInfo(entry)}
                >
                  <LazyImage
                    figureClass={bem('s-team', 'entry-image')}
                    defaultUrl={entry.image.sizes.large}
                    placeholderUrl={entry.image.sizes.placeholder}
                    alt={entry.image.alt}
                  >
                    <LazySource
                      srcSet={entry.image.sizes.large}
                      mediaMin={tailwindConfig.theme.screens.lg}
                    />
                    <LazySource
                      srcSet={entry.image.sizes.medium}
                      mediaMin={tailwindConfig.theme.screens.sm}
                    />
                  </LazyImage>
                  <div className={bem('s-team', 'entry-content')}>
                    <h3 className='h4'>
                      <strong>{entry.name}</strong>
                      <span>{entry.position}</span>
                    </h3>
                    {entry.description && (
                      <p dangerouslySetInnerHTML={{ __html: entry.description }} />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
    </div>
  );
};

Team.propTypes = {
  heading: PropTypes.string,
  button: PropTypes.object,
  entries: PropTypes.array
};

export default Team;
